import { Link } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Navigation from "@/components/Navigation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Book, 
  Heart, 
  Share2, 
  MoreVertical,
  Users,
  TrendingUp,
  Clock,
  Star,
  MessageCircle,
  ThumbsUp,
  Plus,
  Filter,
  Search,
  BookOpen,
  Calendar,
  Award,
  Sparkles,
  Flower,
  Rocket,
  Music
} from "lucide-react";
import { motion } from "framer-motion";

// Types
interface Story {
  id: number;
  title: string;
  description: string;
  sharedDate: string;
  likes: number;
  comments: number;
  views: number;
  tags: string[];
  thumbnail: string;
  author?: string;
  isPublic?: boolean;
}

// Empty arrays - stories will come from API in real implementation
const userSharedStories: Story[] = [];
const communityStories: Story[] = [];

function StoryCard({ story, isUserStory = false }: { story: Story; isUserStory?: boolean }) {
  const getStoryIcon = (iconName: string) => {
    const iconMap: { [key: string]: React.ComponentType<any> } = {
      "book-open": BookOpen,
      "sparkles": Sparkles,
      "flower": Flower,
      "rocket": Rocket,
      "music": Music,
      "clock": Clock
    };
    
    const IconComponent = iconMap[iconName] || BookOpen;
    return <IconComponent className="h-8 w-8" />;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="h-full hover:shadow-lg transition-all duration-300 group">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-100 to-teal-100 flex items-center justify-center flex-shrink-0">
                <div className="text-purple-600">
                  {getStoryIcon(story.thumbnail)}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-base sm:text-lg line-clamp-1">{story.title}</h3>
                <p className="text-xs sm:text-sm text-gray-600">
                  {isUserStory ? "by You" : `by ${story.author}`}
                </p>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem>
                  <BookOpen className="mr-2 h-4 w-4" />
                  Read Story
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Share2 className="mr-2 h-4 w-4" />
                  Share
                </DropdownMenuItem>
                {isUserStory && (
                  <DropdownMenuItem>
                    <Book className="mr-2 h-4 w-4" />
                    Edit Privacy
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          <p className="text-gray-700 text-sm mb-4 line-clamp-2">{story.description}</p>
          
          <div className="flex flex-wrap gap-1 sm:gap-2 mb-4">
            {story.tags.map((tag: string, index: number) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm text-gray-500">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-1">
                <ThumbsUp className="h-4 w-4" />
                <span>{story.likes}</span>
              </div>
              <div className="flex items-center gap-1">
                <MessageCircle className="h-4 w-4" />
                <span>{story.comments}</span>
              </div>
              <div className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                <span>{story.views}</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span className="text-xs sm:text-sm">{new Date(story.sharedDate).toLocaleDateString()}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function CommunityStats() {
  const stats = [
    { label: "Your Shared Stories", value: 0, icon: Book },
    { label: "Total Likes Received", value: 0, icon: Heart },
    { label: "Community Stories", value: 0, icon: Users },
    { label: "This Week's Popular", value: 0, icon: TrendingUp }
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
      {stats.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <Card key={index} className="overflow-hidden">
            <CardContent className="p-3 sm:p-4 text-center">
              <Icon className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-1 sm:mb-2 text-purple-600" />
              <p className="text-lg sm:text-2xl font-bold text-gray-900 leading-none">{stat.value}</p>
              <p className="text-xs sm:text-sm text-gray-600 mt-1 leading-tight">{stat.label}</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default function Community() {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-teal-50">
        <Navigation />
        <div className="container mx-auto px-4 py-12 text-center">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
            Join Our Story Community
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Share your favorite stories with other families and discover amazing tales from our community.
          </p>
          <Link href="/auth">
            <Button size="lg" className="bg-gradient-to-r from-purple-600 to-teal-600 text-white">
              Sign Up to Join Community
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-teal-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
            Story Community
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            Share your stories and discover amazing tales from other families
          </p>
        </div>

        {/* Stats */}
        <CommunityStats />

        {/* Main Content */}
        <Tabs defaultValue="community" className="space-y-4 sm:space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <TabsList className="grid w-full sm:max-w-md grid-cols-2">
              <TabsTrigger value="community" className="flex items-center gap-1 sm:gap-2 text-sm">
                <Users className="h-4 w-4" />
                <span className="hidden sm:inline">Community Stories</span>
                <span className="sm:hidden">Community</span>
              </TabsTrigger>
              <TabsTrigger value="my-shared" className="flex items-center gap-1 sm:gap-2 text-sm">
                <Book className="h-4 w-4" />
                <span className="hidden sm:inline">My Shared Stories</span>
                <span className="sm:hidden">My Stories</span>
              </TabsTrigger>
            </TabsList>
            
            <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto">
              <Button variant="outline" size="sm" className="whitespace-nowrap">
                <Filter className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Filter</span>
              </Button>
              <Button variant="outline" size="sm" className="whitespace-nowrap">
                <Search className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Search</span>
              </Button>
              <Link href="/stories">
                <Button size="sm" className="bg-gradient-to-r from-purple-600 to-teal-600 text-white whitespace-nowrap">
                  <Plus className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Share New Story</span>
                  <span className="sm:hidden">Share</span>
                </Button>
              </Link>
            </div>
          </div>

          <TabsContent value="community" className="space-y-4 sm:space-y-6">
            {communityStories.length > 0 ? (
              <div>
                <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 flex items-center gap-2">
                  <Star className="h-5 w-5 text-yellow-500" />
                  Trending Stories
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {communityStories.map((story) => (
                    <StoryCard key={story.id} story={story} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 sm:py-12">
                <Users className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-4 text-gray-400" />
                <h3 className="text-base sm:text-lg font-semibold mb-2">No community stories yet</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 px-4">
                  Be the first to share a story with the community!
                </p>
                <Link href="/stories">
                  <Button className="bg-gradient-to-r from-purple-600 to-teal-600 text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    Share Your First Story
                  </Button>
                </Link>
              </div>
            )}
          </TabsContent>

          <TabsContent value="my-shared" className="space-y-4 sm:space-y-6">
            {userSharedStories.length > 0 ? (
              <div>
                <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Your Shared Stories</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {userSharedStories.map((story) => (
                    <StoryCard key={story.id} story={story} isUserStory={true} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 sm:py-12">
                <Book className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-4 text-gray-400" />
                <h3 className="text-base sm:text-lg font-semibold mb-2">No shared stories yet</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 px-4">
                  Share your first story with the community to get started!
                </p>
                <Link href="/stories">
                  <Button className="bg-gradient-to-r from-purple-600 to-teal-600 text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    Share Your First Story
                  </Button>
                </Link>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}